import { getJSONByUrl } from 'widgets/toolbox/ajax';
import { appendParamToURL, clickOutside, timeout } from 'widgets/toolbox/util';

/**
 * @typedef {ReturnType <typeof import('widgets/global/AccessibilityFocusTrapMixin').default>} AccessibilityFocusTrapMixin
 * @typedef {ReturnType <typeof import('widgets/global/Refinement').default>} Refinement
 */

/**
 * @param {AccessibilityFocusTrapMixin} AccessibilityFocusTrapMixin base widget for extending
 * @returns {HarmonyRefinementsPanel} HarmonyRefinementsPanel
 */
export default function (AccessibilityFocusTrapMixin) {
    /**
     * @class HarmonyRefinementsPanel
     * @augments ListAccessibility
     */
    class HarmonyRefinementsPanel extends AccessibilityFocusTrapMixin {
        init() {
            // Initialize ListAccessibility instance.
            super.init();

            // Listen emits from refinement toggler widget
            this.eventBus().on('refinement.panel.open', 'openPanel');

            // Listen emits from refinement when it was changed
            this.eventBus().on('refinement.state.changed', 'onChangeRefinement');

            // Listen emits from accordion item for keep expanded refinements opened
            this.eventBus().on('accordion.item.toggled', 'onRefinementToggled');

            // Listen emits from HarmonyProductListingMgr for keep PLP updated event and enable scroll
            this.eventBus().on('plp.updated', 'enableScroll');
        }

        prefs() {
            return {
                ...super.prefs(),

                // Used to show/hide overlay.
                classBackdropActive: 'm-active',

                // Used to show/hide panel.
                classPanelOpened: 'm-opened',

                refTrapsContainer: 'refinementsContainer'
            };
        }

        /**
         * @description update panel within selected category
         * @param {object} target clicked specific category
         * @returns {void}
         */
        changeCategory(target) {
            this.applyRefinements(target.config.refineUrl, false, true, false);
        }

        /**
         * @param target
         * @description store clicked refinement state for keep expanded refinements opened
         */
        onRefinementToggled(target) {
            let refinements = [];

            try {
                refinements = JSON.parse(sessionStorage.getItem('expandedRefinements') || '');
            } catch (e) {
                sessionStorage.setItem('expandedRefinements', JSON.stringify([]));
            }

            const index = refinements.indexOf(target.config.name);

            // remove duplicates
            // eslint-disable-next-line no-bitwise
            if (~index) {
                refinements.splice(index, 1);
            } else {
                refinements.push(target.config.name);
            }

            sessionStorage.removeItem('expandedRefinements');
            sessionStorage.setItem('expandedRefinements', JSON.stringify([...new Set(refinements)]));
        }

        /**
         * @param target
         * @description executes when refinement was changed
         * @returns {void}
         */
        onChangeRefinement(target) {
            this.lastChangedRefinement = target;
            this.applyRefinements(this.collectRefinementUrl(), false, true, false);
        }

        /**
         * @description make panel visible on storefront.
         * @returns {void}
         */
        openPanel() {
            this.disableScroll();

            // Clean opened refinements
            sessionStorage.removeItem('expandedRefinements');
            sessionStorage.setItem('expandedRefinements', JSON.stringify([]));

            this.exposeOutSideClick = clickOutside(this.ref('refinementsContainer'), this.closePanel.bind(this));

            this.getRefinementsModel();
            this.render('template', this.model, this.ref('refinementsContainer'))
                .then(() => {
                    this.show();

                    // Save current refinement selection URL
                    this.currentRefinementUrl = this.collectRefinementUrl();
                });
        }

        /**
         * @description make panel invisible on storefront.
         * @returns {boolean}
         */
        closePanel() {
            this.enableScroll();
            sessionStorage.removeItem('expandedRefinements');

            this.hide();
            this.expose();

            this.eventBus().emit('refinement.panel.close');
        }

        /**
         * @description apply selected filters, trigger PLP update view, close the panel.
         * @returns {void}
         */
        applyFilters() {
            const refinementUrl = this.collectRefinementUrl();

            // Check if refinement selection was changed
            if (refinementUrl !== this.currentRefinementUrl) {
                this.currentRefinementUrl = refinementUrl;
                this.applyRefinements(refinementUrl);
                this.expose();
            } else {
                this.closePanel();
            }
        }

        /**
         * @description collect refinebar update url with selected refinements.
         * @returns {string} url
         */
        collectRefinementUrl() {
            const refinements = this.getSelectedRefinements();
            const keys = Object.keys(refinements);

            let count = 1;
            let url = this.ref('refinementsContainer').get(0).dataset.refinebarUrl;

            keys.forEach(key => {
                const refinement = refinements[key];

                if (key === 'price') {
                    const prices = refinement.values.split('|');
                    url = appendParamToURL(url, 'pmin', prices[0]);
                    url = appendParamToURL(url, 'pmax', prices[1]);
                    return;
                }

                url = appendParamToURL(url, 'prefn'.concat(count), key);
                url = appendParamToURL(url, 'prefv'.concat(count), refinement.values);

                count++;
            });

            return url;
        }

        /**
         * @description clear filter refinements and hide panel.
         */
        clearFilters() {
            this.uncheckRefinements();
            this.applyFilters();
            this.expose();
        }

        /**
         * @description get refinements info according to the selected refinement values
         * @param {string} url
         * @param {boolean} updateGrid is update product grid
         * @param {boolean} updatePanel is update panel view
         * @param {boolean} updateRefinementModel is update refinement model
         * @returns {void}
         */
        applyRefinements(url, updateGrid = true, updatePanel = false, updateRefinementModel = true) {
            this.ref('self').attr('aria-busy', 'true');

            const promise = getJSONByUrl(url).then(refinementsModel => {
                if (Object.keys(refinementsModel).length && refinementsModel.count) {
                    this.ref('self').attr('aria-busy', 'false');

                    if (updateRefinementModel) {
                        this.setRefinementsModel(refinementsModel);
                    }

                    if (updatePanel) {
                        this.setTargetElementPosition();
                        this.render(
                            'template',
                            this.syncExpandedRefinements(refinementsModel),
                            this.ref('refinementsContainer')
                        ).then(() => {
                            this.ref('refinementsContainer').addClass(this.prefs().classPanelOpened);
                        }).finally(() => {
                            this.restoreScrollPosition();
                        });
                    }

                    if (updateGrid) {
                        this.closePanel();
                        this.eventBus().emit('refinement.update.plp', refinementsModel.ajaxUrl, undefined, true);
                    }
                } else {
                    this.emit('noresult');
                }
            });

            this.eventBus().emit('loader.start', promise);
        }

        setTargetElementPosition() {
            this.changedRefinementTop = this.lastChangedRefinement.ref('self').get().getBoundingClientRect().top;
        }

        restoreScrollPosition() {
            let element = this.lastChangedRefinement;

            // Find refinement which has been last changed.
            this.getAllRefinements().some((refinement) => {
                if (
                    refinement.config.attrId === element.config.attrId
                    && refinement.config.attrValue === element.config.attrValue
                ) {
                    element = refinement;
                    return true;
                }
                return false;
            });

            // el.offsetTop -- current element offset position
            // this.changedRefinementTop -- stored previous element window position
            this.ref('refinementsContent').get().scrollTop = element.ref('self').get().offsetTop - this.changedRefinementTop;
        }

        /**
         * @description update refinement model in script tag
         * @param {object} refinementModel
         * @returns {void}
         */
        setRefinementsModel(refinementModel) {
            this.ref('refinementsModel').setText(JSON.stringify(refinementModel));
        }

        /**
         * @param model
         * @description get expanded refinement from the storage and update the backend model
         * according to the specified data.
         * @returns {object} model : refinement model
         */
        syncExpandedRefinements(model) {
            const expandedRefinements = JSON.parse(sessionStorage.getItem('expandedRefinements'));

            if (expandedRefinements.length <= 0) {
                return model;
            }

            model?.cachedRefinements?.forEach(refinement => {
                if (refinement.isExpanded) { return; }

                // eslint-disable-next-line no-bitwise
                if (~expandedRefinements.indexOf(refinement.refinementId)) {
                    refinement.isExpanded = true;
                }
            });

            return model;
        }

        /**
         * @description get refinement model from html to the component instance.
         * @param {object} refinementsModel
         * @returns {void}
         */
        getRefinementsModel() {
            var model = this.ref('refinementsModel').getText().trim() || '';
            try {
                model = JSON.parse(`"${model}"`);
                this.model = JSON.parse(model);
            } catch (e) {
                this.model = {};
            }
        }

        /**
         * @description get selected refinements and it's values
         *
         * refinements = {
         *      color: {
         *          values: 'black'
         *      },
         *      size: {
         *          values: 'XL|X'
         *      },
         *      price: {
         *          values: '0|100'
         *      }
         * }
         *
         * @returns {object} object with selected refinements and it values.
         */
        getSelectedRefinements() {
            const refinements = {};

            this.getAllRefinements().forEach(/** @type {Refinement} */(refinement) => {
                if (refinement.selected) {
                    const type = refinement.config.attrId;
                    const refinementValue = refinement.config.attrValue + '';

                    // Initialize with empty object if
                    // refinement at first in object
                    if (!refinements[type]) {
                        refinements[type] = {
                            values: ''
                        };
                    }

                    if (type === 'price') {
                        refinements[type].values = [refinement.min, refinement.max].join('|');
                    } else {
                        refinements[type].values = refinements[type].values.length
                            ? refinements[type].values.concat('|').concat(refinementValue)
                            : refinementValue;
                    }
                }
            });

            return refinements;
        }

        /**
         * @description go through component items and fetch all refinements.
         * @returns {Array<refinement>} Array of refinement widgets
         */
        getAllRefinements() {
            const Refinement = /** @type {Refinement} */ (this.getConstructor('refinement'));
            const AccordionItemPanel = /** @type {AccordionItemPanel} */ (this.getConstructor('accordionItemPanel'));
            const refinements = [];

            // As widget see only his children we need to get refinements this way.
            // Current widget -> Accordion widget -> Accordion item widget -> Refinements widgets
            this.getById('accordion', accordion => {
                accordion.items?.map((item) => {
                    if (item instanceof AccordionItemPanel) {
                        item.items?.map((child) => {
                            if (child instanceof Refinement && child.isAttributeRefinement()) {
                                refinements.push(child);
                            }
                        });
                    }
                });
            });

            return refinements;
        }

        /**
         * @description uncheck all component refinements refinements.
         * @returns {void}
         */
        uncheckRefinements() {
            this.getAllRefinements().forEach((refinement) => {
                refinement.uncheck();
            });
        }

        /**
         * @description show current component view.
         * @returns {void}
         */
        show() {
            super.show();

            this.ref('self').addClass(this.prefs().classBackdropActive);
            this.ref('refinementsContainer').addClass(this.prefs().classPanelOpened);

            this.afterShowModal();
        }

        /**
         * @description hide current component view.
         * @returns {void}
         */
        hide() {
            this.ref('self').removeClass(this.prefs().classBackdropActive);
            this.ref('refinementsContainer').removeClass(this.prefs().classPanelOpened);
        }

        /**
         * @description removes document event listener for overlay our side clicks.
         * @returns {void}
         */
        expose() {
            if (typeof this.exposeOutSideClick === 'function') {
                this.exposeOutSideClick();
            }
            timeout(() => this.ref('refinementsContainer').empty(), this.prefs().focusTimeout);
        }

        disableScroll() {
            this.body = document.body;
            this.scrollTop = window.scrollY;

            if (!this.body.classList.contains(this.prefs().classesGlobalDialog)) {
                this.body.classList.add(this.prefs().classesGlobalDialog);
            }

            this.body.dataset.scrollTop = this.scrollTop;
            this.body.style.top = `-${this.scrollTop}px`;
            document.documentElement.scrollTop = 0;
        }

        enableScroll() {
            if (this.body) {
                this.body.classList.remove(this.prefs().classesGlobalDialog);
                this.body.style.removeProperty('top');
                this.body.removeAttribute('data-scroll-top');
                window.scrollTo({
                    top: this.scrollTop,
                    behavior: 'instant'
                });
                this.scrollTop = 0;
            }
        }
    }

    return HarmonyRefinementsPanel;
}
